import React, { useState, useEffect } from 'react';
import './XTool.css';
import supabase from '../components/supabaseClient';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../components/AuthContext';
import CloseIcon from '@mui/icons-material/Close';
import TrashIcon from '@mui/icons-material/Delete';

const XSavedSearches = ({ onSelectSearch, onNewSearch, preloadedSearches = [], isLoading = false }) => {
    const [searches, setSearches] = useState([]);
    const [loading, setLoading] = useState(true);
    const { userId } = useAuth();
    const [filter, setFilter] = useState('');

    // Use preloaded searches if available
    useEffect(() => {
        if (preloadedSearches && preloadedSearches.length > 0) {
            console.log("Using preloaded searches:", preloadedSearches.length);
            setSearches(preloadedSearches);
            setLoading(false);
        } else if (!isLoading) {
            // Only fetch if we don't have preloaded searches and we're not already loading
            fetchSearches();
        }
    }, [preloadedSearches, isLoading]);

    // Fetch saved searches from Supabase
    const fetchSearches = async () => {
        if (!userId) {
            console.log("No userId available, skipping fetch");
            return;
        }
        
        setLoading(true);
        console.log("Fetching saved searches for user:", userId);
        
        try {
            const { data, error } = await supabase
                .from('saved_searches')
                .select('*')
                .eq('user_id', userId)
                .eq('type', 'twitter')
                .order('created_at', { ascending: false });
                
            if (error) {
                console.error('Error in fetchSearches query:', error);
                throw error;
            }
            
            console.log("Saved searches data received:", data);
            
            if (data) {
                console.log(`Found ${data.length} saved searches`);
                setSearches(data);
            } else {
                console.log("No data returned from query");
                setSearches([]);
            }
        } catch (error) {
            console.error('Error fetching searches:', error);
        } finally {
            setLoading(false);
        }
    };

    // Delete a saved search
    const handleDelete = async (searchId) => {
        if (!window.confirm('Are you sure you want to delete this saved search?')) {
            return;
        }
        
        try {
            const { error } = await supabase
                .from('saved_searches')
                .delete()
                .eq('id', searchId)
                .eq('user_id', userId);
                
            if (error) throw error;
            
            // Update local state
            setSearches(prevSearches => prevSearches.filter(search => search.id !== searchId));
            
        } catch (error) {
            console.error('Error deleting search:', error);
        }
    };

    // Convert database row to search details format expected by other components
    const formatSearchDetails = (search) => {
        console.log("Formatting search details for:", search);
        
        try {
            // Handle both possible formats: params may be a string that needs parsing or already an object
            let params = search.search_params;
            
            if (typeof params === 'string') {
                try {
                    params = JSON.parse(params);
                } catch (e) {
                    console.error("Failed to parse search_params string:", e);
                }
            }
            
            const formattedSearch = {
                id: search.id,
                params: params,
                link: search.link,
                name: search.name
            };
            
            console.log("Formatted search details:", formattedSearch);
            return formattedSearch;
        } catch (error) {
            console.error("Error in formatSearchDetails:", error, "for search:", search);
            // Return a basic object to avoid crashes
            return {
                id: search.id,
                params: {},
                link: search.link || "",
                name: search.name || "Unnamed Search"
            };
        }
    };

    // Handle edit button click
    const handleEdit = (event, search) => {
        event.stopPropagation();
        onSelectSearch(formatSearchDetails(search));
    };

    // Run a search directly
    const handleRunSearch = (search) => {
        window.open(search.link, '_blank');
    };

    // Filter saved searches based on search query
    const filteredSavedSearches = searches.filter(search => {
        console.log("Filtering search:", search);
        
        if (!search || !search.name) {
            console.log("Invalid search item:", search);
            return false;
        }
        
        const nameMatch = search.name.toLowerCase().includes(filter.toLowerCase());
        console.log("Search:", search.name, "matches filter:", filter, "=", nameMatch);
        return nameMatch;
    });

    console.log("Total searches:", searches.length, "Filtered searches:", filteredSavedSearches.length);

    return (
        <div className="xtool-saved-searches">
            <div className="xtool-saved-header">
                <button className="xtool-back-button" onClick={onNewSearch}>
                    <ArrowBackIcon />
                    <span>Back to Search</span>
                </button>
            </div>
            <h2>Saved Searches</h2>

            
            <div className="xtool-search-bar">
                <input
                    type="text"
                    placeholder="Search saved items..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="xtool-search-input"
                />
                {filter && (
                    <button className="xtool-clear-search" onClick={() => setFilter("")}>
                        <CloseIcon />
                    </button>
                )}
            </div>
            
            {loading ? (
                <div className="xtool-loading">Loading saved searches...</div>
            ) : searches.length === 0 ? (
                <div className="xtool-no-saved">
                    <p>No saved searches yet. Save a search to see it here.</p>
                    <button className="xtool-new-search-button" onClick={onNewSearch}>
                        <AddCircleIcon />
                        <span>Create New Search</span>
                    </button>
                </div>
            ) : (
                <>
                    <div className="xtool-saved-list">
                        {filteredSavedSearches.length === 0 ? (
                            <div className="xtool-no-saved">
                                {filter ? (
                                    <p>No saved searches matching "{filter}"</p>
                                ) : (
                                    <p>No saved searches yet. Save a search to see it here.</p>
                                )}
                            </div>
                        ) : (
                            filteredSavedSearches.map((search) => (
                                <div 
                                    className="xtool-saved-item" 
                                    key={search.id}
                                    onClick={() => handleRunSearch(search)}
                                    title="Click to run this search"
                                >
                                    <div className="xtool-saved-info">
                                        <div className="xtool-saved-query">{search.name || 'Unnamed Search'}</div>
                                        <div className="xtool-saved-date">{new Date(search.created_at).toLocaleDateString()}</div>
                                    </div>
                                    <div className="xtool-saved-actions">
                                        <button 
                                            className="xtool-icon-action-button" 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEdit(e, search);
                                            }}
                                            title="Edit search"
                                        >
                                            <EditIcon />
                                        </button>
                                        <button 
                                            className="xtool-icon-action-button" 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(search.id);
                                            }}
                                            title="Delete search"
                                        >
                                            <TrashIcon />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                    
                    <button className="xtool-new-search-button" onClick={onNewSearch}>
                        <AddCircleIcon />
                        <span>Create New Search</span>
                    </button>
                </>
            )}
        </div>
    );
};

export default XSavedSearches; 